import React, { useState, useEffect } from 'react';
import { SignInWithApple } from './SignInButton';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import AuthLoadingScreen from './AuthLoadingScreen';
import { useAppleAuth } from '../hooks/useAppleAuth';
import './LoginScreen.css';

const Container = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const Title = styled(motion.h1)`
  font-size: ${({ theme }) => theme.typography.h1.fontSize};
  font-weight: ${({ theme }) => theme.typography.h1.fontWeight};
  margin-bottom: 24px;
  text-align: center;
  background: linear-gradient(90deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Subtitle = styled(motion.p)`
  font-size: ${({ theme }) => theme.typography.body1.fontSize};
  margin-bottom: 40px;
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const LogoContainer = styled(motion.div)`
  margin-bottom: 40px;
`;

const Logo = styled.img`
  width: 80px;
  height: 80px;
`;

const ErrorMessage = styled(motion.div)`
  margin-top: 20px;
  padding: 12px 16px;
  background: ${({ theme }) => theme.colors.error}20;
  color: ${({ theme }) => theme.colors.error};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  border-left: 4px solid ${({ theme }) => theme.colors.error};
`;

export const LoginScreen = () => {
  const { login, loading, error: authError, user, isRedirectCompleted } = useAppleAuth();
  const [errorMessage, setErrorMessage] = useState(null);
  
  useEffect(() => {
    if (user) {
      if (user.isNewUser) {
        window.location.href = '/onboarding';
      } else {
        window.location.href = '/dashboard';
      }
    }
    
    if (authError) {
      setErrorMessage(authError);
    }
  }, [user, authError]);
  
  if (loading && !isRedirectCompleted) {
    return <AuthLoadingScreen />;
  }

  const handleAppleSignIn = async () => {
    try {
      setErrorMessage(null);
      await login();
    } catch (err) {
      console.error('Ошибка при входе через Apple:', err);
      setErrorMessage('Произошла ошибка при входе через Apple');
    }
  };

  return (
    <Container
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
    >
      <LogoContainer
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.6 }}
      >
        <Logo src="/logo.png" alt="Logo" />
      </LogoContainer>
      
      <Title
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.3, duration: 0.6 }}
      >
        Начните Свое Кулинарное Приключение
      </Title>
      
      <Subtitle
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.4, duration: 0.6 }}
      >
        Получите доступ к персонализированным рецептам
      </Subtitle>
      
      {errorMessage && (
        <ErrorMessage
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          {errorMessage}
        </ErrorMessage>
      )}
      
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.5, duration: 0.6 }}
      >
        <SignInWithApple onClick={handleAppleSignIn} disabled={loading} />
      </motion.div>
    </Container>
  );
}; 